/**
 * @engine/input — Deadzone Processor
 *
 * Applies deadzone to axis/stick values.
 */

import type { ProcessorType, ControlValue, DeadzoneParams } from '../types';
import { InputProcessor, registerProcessor } from './InputProcessor';

/**
 * Radial deadzone for sticks (Vec2)
 * Applies inner and outer deadzone, rescales to 0..1
 */
export class DeadzoneProcessor extends InputProcessor {
  readonly type: ProcessorType = 'deadzone';

  private readonly min: number;
  private readonly max: number;

  constructor(params: Partial<DeadzoneParams> = {}) {
    super(params as Record<string, unknown>);
    this.min = params.min ?? 0.125;
    this.max = params.max ?? 0.925;
  }

  process(value: ControlValue): ControlValue {
    if (typeof value === 'number') {
      return this.processAxis(value);
    }

    if (this.isVec2(value)) {
      return this.processStick(value);
    }

    return value;
  }

  private processAxis(value: number): number {
    const absValue = Math.abs(value);
    const sign = Math.sign(value);

    if (absValue < this.min) return 0;
    if (absValue > this.max) return sign;

    // Rescale to 0..1
    return sign * (absValue - this.min) / (this.max - this.min);
  }

  private processStick(value: { x: number; y: number }): { x: number; y: number } {
    const mag = Math.sqrt(value.x * value.x + value.y * value.y);

    if (mag < this.min) return { x: 0, y: 0 };

    // Direction
    const dirX = value.x / mag;
    const dirY = value.y / mag;

    // Rescaled magnitude
    const clampedMag = Math.min(this.max, mag);
    const rescaledMag = (clampedMag - this.min) / (this.max - this.min);

    return {
      x: dirX * rescaledMag,
      y: dirY * rescaledMag,
    };
  }
}

/**
 * Axis deadzone (for individual axes, not radial)
 */
export class AxisDeadzoneProcessor extends InputProcessor {
  readonly type: ProcessorType = 'axisDeadzone';

  private readonly min: number;
  private readonly max: number;

  constructor(params: Partial<DeadzoneParams> = {}) {
    super(params as Record<string, unknown>);
    this.min = params.min ?? 0.125;
    this.max = params.max ?? 0.925;
  }

  process(value: ControlValue): ControlValue {
    if (typeof value === 'number') {
      const absValue = Math.abs(value);
      const sign = Math.sign(value);

      if (absValue < this.min) return 0;
      if (absValue > this.max) return sign;

      return sign * (absValue - this.min) / (this.max - this.min);
    }

    // For Vec2, apply per-axis
    if (this.isVec2(value)) {
      return {
        x: this.process(value.x) as number,
        y: this.process(value.y) as number,
      };
    }

    return value;
  }
}

/**
 * Stick deadzone (radial)
 */
export class StickDeadzoneProcessor extends DeadzoneProcessor {
  override readonly type: ProcessorType = 'stickDeadzone';
}

// Register processors
registerProcessor('deadzone', DeadzoneProcessor);
registerProcessor('axisDeadzone', AxisDeadzoneProcessor);
registerProcessor('stickDeadzone', StickDeadzoneProcessor);
